import AddIcon from "@mui/icons-material/Add";
import { Box, Popover, Stack } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { CustomCellEditorProps } from "ag-grid-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import {
  type BenefitTypeOption,
  ChildAgeBandsField,
} from "./ChildAgeBandsField";
import { type ChildAgeBand } from "./childAgeBandValues";
import { useChildAgeBands } from "./useChildAgeBands";

export interface ChildAgeBandsEditorParams {
  typeOptions: BenefitTypeOption[];
}

export interface ChildAgeBandsEditorRef {
  getValue: () => ChildAgeBand[];
}

const parseInitialBands = (raw: unknown): ChildAgeBand[] => {
  if (Array.isArray(raw)) {
    return raw as ChildAgeBand[];
  }
  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      return JSON.parse(raw) as ChildAgeBand[];
    } catch {
      return [];
    }
  }
  return [];
};

interface EditableWhenColDef {
  editableWhen?: { dependsOn: string; disabledValues?: unknown[] };
}

const computeIsDisabled = (
  editableWhen: EditableWhenColDef["editableWhen"],
  nodeData: Record<string, unknown> | undefined,
): boolean => {
  if (!editableWhen || !nodeData) return false;
  const disabledValues = editableWhen.disabledValues || [];
  const dependsOnValue = nodeData[editableWhen.dependsOn];
  return disabledValues.includes(dependsOnValue);
};

const resolveEditorParams = (
  props: CustomCellEditorProps<Record<string, unknown>, unknown, unknown>,
): ChildAgeBandsEditorParams => {
  // AG Grid passes cellEditorParams through the params object when using
  // cellEditorSelector, same dual-path read as PopoverListEditor.
  const editorParams =
    (props as unknown as { cellEditorParams?: unknown }).cellEditorParams ??
    props;
  const params = editorParams as ChildAgeBandsEditorParams;
  return { typeOptions: params?.typeOptions ?? [] };
};

/**
 * Build's popup editor for the "Benefit Description & Ages - CH" cell
 * (`childBenefitAmountsByAge`, gated by the `btx-ch-benefits-field` flag). Edits
 * the full child age-band array: any number of bands, each with its own
 * lower/upper age-type (unit) and limit plus a benefit type and amount. Shares
 * its state, validation, and band-list UI with Present's
 * ChildBenefitAgeBandsCellEditor via useChildAgeBands / ChildAgeBandsField —
 * this shell only supplies Build's own popup chrome and no custom-text mode.
 */
export const ChildAgeBandsEditor = forwardRef<
  ChildAgeBandsEditorRef,
  CustomCellEditorProps<Record<string, unknown>, unknown, unknown>
>((props, ref) => {
  const { stopEditing, api, node, column, value } = props;
  const { typeOptions } = resolveEditorParams(props);
  const initialBands = parseInitialBands(value);

  const editor = useChildAgeBands(initialBands);
  const lastValueRef = useRef<ChildAgeBand[]>(initialBands);

  const colDef = column?.getColDef() as unknown as
    | EditableWhenColDef
    | undefined;
  const isDisabled = computeIsDisabled(
    colDef?.editableWhen,
    node?.data as Record<string, unknown> | undefined,
  );

  useEffect(() => {
    if (isDisabled && stopEditing) {
      stopEditing();
    }
  }, [isDisabled, stopEditing]);

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    getValue: () => lastValueRef.current,
  }));

  // Auto-open popover when editor is created (only if not disabled). The
  // timeout is load-bearing: anchorRef.current is null on the first render, so
  // opening synchronously would anchor the Popover to null and center it on
  // screen instead of under the cell.
  useEffect(() => {
    if (!isDisabled) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 10);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isDisabled]);

  const handleCancel = (): void => {
    lastValueRef.current = initialBands;
    setOpen(false);
    if (stopEditing) stopEditing(true);
  };

  const handleSave = (): void => {
    const saveValue = editor.getSaveValue();
    if (saveValue === null) {
      return;
    }

    lastValueRef.current = saveValue;
    setOpen(false);
    if (stopEditing) stopEditing(true);

    // stopEditing(true) cancels AG Grid's own commit, so persist explicitly —
    // mirrors Present's ChildBenefitAgeBandsCellEditor save path.
    setTimeout(() => {
      if (node && column) {
        node.setDataValue(column.getColId(), saveValue);
      }
      if (api && node) {
        api.refreshCells({ rowNodes: [node], force: true });
      }
    }, 0);
  };

  const handleEnterKey = (event: ReactKeyboardEvent): void => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      handleSave();
    }
  };

  const handleAddBand = (): void => {
    editor.addBand();
  };

  if (isDisabled) {
    return <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />;
  }

  return (
    <>
      <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableEnforceFocus
        disableAutoFocus={false}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              minWidth: 400,
              maxWidth: 600,
              maxHeight: 600,
              overflow: "auto",
            },
          },
        }}
      >
        <Box
          sx={{ p: 2 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleEnterKey}
        >
          <Typography
            variant="subtitle1"
            sx={{ mb: 1.5 }}
            color="textSecondary"
          >
            <strong>Benefit Description &amp; Ages - CH</strong>
          </Typography>

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <ChildAgeBandsField
              bands={editor.bands}
              typeOptions={typeOptions}
              showErrors={editor.showErrors}
              onUpdate={editor.updateBand}
              onRemove={editor.removeBand}
              onAmountUpdate={editor.updateAmount}
              onAmountAdd={editor.addAmount}
              onAmountRemove={editor.removeAmount}
            />
          </Stack>

          <Button
            size="sm"
            variant="tertiary"
            startIcon={<AddIcon />}
            onClick={handleAddBand}
            sx={{ alignSelf: "flex-start", mb: 2 }}
          >
            Add band
          </Button>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
});

ChildAgeBandsEditor.displayName = "ChildAgeBandsEditor";
